import React, { useEffect, useRef, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '../../context/I18nContext';
import CloseButton from './CloseButton';
import { isTopModal, modalZIndex, registerModal } from '../../utils/modalStack';

const ENTER_MS = 320;
const EXIT_MS = 180;
const BACKDROP_ENTER_MS = 420;
/** Soft ease-out — less “pop”, more float in. */
const ENTER_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const BACKDROP_ENTER_EASE = 'cubic-bezier(0.33, 1, 0.68, 1)';
/** Slightly tighter exit so dismiss feels immediate. */
const EXIT_EASE = 'cubic-bezier(0.4, 0, 1, 1)';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  /** Sticky actions below the scrollable body (recommended for forms). */
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  showCloseButton?: boolean;
  /** Extra classes on the scrollable body wrapper. */
  bodyClassName?: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  showCloseButton = true,
  bodyClassName = '',
}) => {
  const { t } = useI18n();
  const modalRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const modalInstanceId = useRef(0);
  const onCloseRef = useRef(onClose);
  const [rendered, setRendered] = useState(isOpen);
  const [visible, setVisible] = useState(false);
  const [stackDepth, setStackDepth] = useState(0);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Keep mounted through exit animation
  useEffect(() => {
    if (isOpen) {
      setRendered(true);
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
      return () => cancelAnimationFrame(raf);
    }

    setVisible(false);
    const timer = window.setTimeout(() => setRendered(false), EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    previousActiveElement.current = document.activeElement as HTMLElement;
    const registration = registerModal();
    modalInstanceId.current = registration.id;
    setStackDepth(registration.depth);

    return registration.unregister;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (!isTopModal(modalInstanceId.current)) return;
      e.stopPropagation();
      onCloseRef.current();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  useEffect(() => {
    if (!rendered) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [rendered]);

  useEffect(() => {
    if (!isOpen || !visible || !modalRef.current) return;

    const modal = modalRef.current;
    const focusableElements = modal.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    firstElement?.focus();

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    };

    modal.addEventListener('keydown', handleTab);
    return () => modal.removeEventListener('keydown', handleTab);
  }, [isOpen, visible]);

  // Restore focus after the panel has fully unmounted (post exit animation)
  useEffect(() => {
    if (rendered || isOpen) return;
    previousActiveElement.current?.focus?.({ preventScroll: true });
  }, [rendered, isOpen]);

  if (!rendered) return null;

  // Full-width on mobile (bottom sheet); constrained width from sm up.
  const sizeClasses = {
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-md',
    lg: 'sm:max-w-lg',
    xl: 'sm:max-w-2xl',
    full: 'sm:max-w-4xl',
  };

  const hasHeader = Boolean(title || showCloseButton);
  const interactive = isOpen && visible;
  const panelDurationMs = visible ? ENTER_MS : EXIT_MS;
  const panelEasing = visible ? ENTER_EASE : EXIT_EASE;
  const backdropDurationMs = visible ? BACKDROP_ENTER_MS : EXIT_MS;
  const backdropEasing = visible ? BACKDROP_ENTER_EASE : EXIT_EASE;

  const modalContent = (
    <div
      className="fixed inset-0"
      style={{ zIndex: modalZIndex(stackDepth) }}
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={descriptionId}
    >
      <div
        className={`fixed inset-0 bg-gray-900/35 backdrop-blur-sm transition-opacity ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          transitionDuration: `${backdropDurationMs}ms`,
          transitionTimingFunction: backdropEasing,
        }}
        onClick={interactive ? onClose : undefined}
        aria-hidden="true"
      />

      <div className="pointer-events-none fixed inset-0 flex max-h-[100dvh] items-end justify-center p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:items-center sm:p-4">
        <div
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          className={`
            pointer-events-auto relative flex w-full flex-col
            max-h-full overflow-hidden
            bg-white shadow-2xl shadow-gray-900/10
            rounded-xl
            will-change-transform
            transition-[opacity,transform]
            ${
              visible
                ? 'translate-y-0 opacity-100 sm:scale-100'
                : 'translate-y-3 opacity-0 sm:translate-y-1 sm:scale-[0.985]'
            }
            ${!interactive ? 'pointer-events-none' : ''}
            ${sizeClasses[size]}
          `}
          style={{
            transitionDuration: `${panelDurationMs}ms`,
            transitionTimingFunction: panelEasing,
          }}
        >
          {hasHeader && (
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-100 px-4 pb-3 pt-4 sm:px-6 sm:pt-5">
              {title ? (
                <h3 id={titleId} className="min-w-0 text-lg font-semibold text-gray-900">
                  {title}
                </h3>
              ) : (
                <span className="flex-1" />
              )}
              {showCloseButton && (
                <CloseButton onClick={onClose} aria-label={t('common.close') || 'Fermer'} />
              )}
            </div>
          )}

          <div
            id={descriptionId}
            className={`min-h-0 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 ${bodyClassName}`}
          >
            {children}
          </div>

          {footer ? (
            <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-3 sm:px-6 sm:py-4">
              {footer}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : null;
};

interface ModalFooterProps {
  children: React.ReactNode;
  className?: string;
}

/** Use inside Modal `footer` prop (or rarely inline). */
export const ModalFooter: React.FC<ModalFooterProps> = ({ children, className = '' }) => {
  return (
    <div className={`flex items-center justify-end gap-3 ${className}`}>
      {children}
    </div>
  );
};

export default Modal;
